import { DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Subscription, filter } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  hospitalityReservationsTopic,
  liveStatusTranslationKey
} from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogService, HospitalityReservation } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-hospitality-reservations',
  imports: [DecimalPipe, AinPageComponent, AinStateComponent, TableModule, ButtonModule, FormsModule, InputTextModule],
  template: `
    <ain-page [title]="t('catalog.hospitality.reservations.title')" [description]="t('catalog.hospitality.reservations.description')">
      @if (loading() && rows().length === 0) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <input pInputText [(ngModel)]="searchTerm" [placeholder]="t('catalog.hospitality.searchReservations')" (keyup.enter)="load()" />
            <button pButton type="button" [label]="t('catalog.action.create')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}
            </span>
          </div>
          <p-table [value]="rows()" [paginator]="true" [rows]="15">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 3rem"><input type="checkbox" aria-label="select all" /></th>
                <th>{{ t('catalog.hospitality.reservationReference') }}</th>
                <th>{{ t('catalog.service.customerReference') }}</th>
                <th>{{ t('catalog.tracking.status') }}</th>
                <th>{{ t('catalog.hospitality.estimatedTotal') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><input type="checkbox" [attr.aria-label]="row.reservationReference" /></td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.reservationReference }}</button></td>
                <td>{{ row.customerReference }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.estimatedTotal | number:'1.2-2' }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; align-items: center; }
    .live-status {
      font-size: 0.85rem;
      font-weight: 600;
      padding: 4px 10px;
      border-radius: 999px;
      background: #f3f4f6;
      color: #6b7280;
    }
    .live-status.connected { background: #dcfce7; color: #166534; }
    .live-status.connecting, .live-status.reconnecting { background: #fef9c3; color: #854d0e; }
    .live-status.disconnected { background: #fee2e2; color: #991b1b; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
  `]
})
export class HospitalityReservationsComponent implements OnInit, OnDestroy {
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusTranslationKey = liveStatusTranslationKey;
  private readonly service = inject(CatalogService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private liveEventsSubscription: Subscription | null = null;
  private subscribedTopic = '';

  readonly loading = signal(true);
  readonly rows = signal<HospitalityReservation[]>([]);
  searchTerm = '';

  ngOnInit(): void {
    this.liveEventsSubscription = this.liveUpdateService.events$
      .pipe(filter(event => event.eventType === LiveUpdateEventTypes.hospitalityReservationsChanged))
      .subscribe(() => this.load(false));

    void this.setupLiveSubscription();
    this.load();
  }

  ngOnDestroy(): void {
    this.liveEventsSubscription?.unsubscribe();
    void this.unsubscribeFromTopic();
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.service.listHospitalityReservations(undefined, undefined, this.searchTerm.trim() || undefined).subscribe({
      next: rows => { this.rows.set(rows); this.loading.set(false); },
      error: () => { this.rows.set([]); this.loading.set(false); }
    });
  }

  openDetail(id: string): void { void this.router.navigate(['/hospitality/reservations', id]); }
  openCreate(): void { void this.router.navigate(['/hospitality/reservations', 'new']); }

  t(key: string): string { return this.localization.translate(key); }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.contextService.context()?.tenantId;
    if (!tenantId) return;

    this.subscribedTopic = hospitalityReservationsTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }

  private async unsubscribeFromTopic(): Promise<void> {
    if (!this.subscribedTopic) return;
    await this.liveUpdateService.unsubscribe(this.subscribedTopic);
    this.subscribedTopic = '';
  }
}
